<template>
  <div class="contact-page">
    <!-- Header -->
    <section class="page-header">
      <div class="container">
        <h1>{{ t('contact.title') }}</h1>
        <p class="page-subtitle">{{ t('contact.subtitle') }}</p>
      </div>
    </section>

    <!-- Contact Content -->
    <section class="contact-content">
      <div class="container">
        <div class="contact-wrapper">
          <!-- Contact Form -->
          <div class="contact-form-section">
            <h2>{{ t('contact.sendMessage') }}</h2>
            <form @submit.prevent="submitForm" class="contact-form">
              <div class="form-group">
                <label for="name">{{ t('contact.name') }}</label>
                <input
                  id="name"
                  v-model="formData.name"
                  type="text"
                  :placeholder="t('contact.namePlaceholder')"
                  required
                />
              </div>
              <div class="form-group">
                <label for="email">{{ t('contact.email') }}</label>
                <input
                  id="email"
                  v-model="formData.email"
                  type="email"
                  :placeholder="t('contact.emailPlaceholder')"
                  required
                />
              </div>
              <div class="form-group">
                <label for="subject">{{ t('contact.subject') }}</label>
                <select v-model="formData.subject" id="subject" required>
                  <option value="">{{ t('contact.selectSubject') }}</option>
                  <option value="bug">{{ t('contact.subjectBug') }}</option>
                  <option value="feature">{{ t('contact.subjectFeature') }}</option>
                  <option value="feedback">{{ t('contact.subjectFeedback') }}</option>
                  <option value="other">{{ t('contact.subjectOther') }}</option>
                </select>
              </div>
              <div class="form-group">
                <label for="message">{{ t('contact.message') }}</label>
                <textarea
                  id="message"
                  v-model="formData.message"
                  :placeholder="t('contact.messagePlaceholder')"
                  rows="6"
                  required
                ></textarea>
              </div>
              <button type="submit" class="btn-submit">{{ t('buttons.send') }}</button>
              <div v-if="submitMessage" :class="['submit-message', submitStatus]">
                {{ submitMessage }}
              </div>
            </form>
          </div>

          <!-- Contact Info -->
          <div class="contact-info-section">
            <h2>{{ t('contact.getInTouch') }}</h2>
            <div class="info-card">
              <div class="info-icon">✉️</div>
              <h3>{{ t('contact.email') }}</h3>
              <p><a href="mailto:contact@upgame.top">contact@upgame.top</a></p>
            </div>
            <div class="info-card">
              <div class="info-icon">🌐</div>
              <h3>{{ t('contact.website') }}</h3>
              <p><a href="https://upgame.top" target="_blank">upgame.top</a></p>
            </div>
            <div class="info-card">
              <div class="info-icon">📍</div>
              <h3>{{ t('contact.responseTime') }}</h3>
              <p>{{ t('contact.responseTimeDesc') }}</p>
            </div>
          </div>
        </div>

        <!-- Back Button -->
        <button class="btn-back" @click="goHome">{{ t('buttons.backHome') }}</button>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from '@/utils/i18n'

const { t } = useI18n()
const formData = ref({
  name: '',
  email: '',
  subject: '',
  message: ''
})

const submitMessage = ref('')
const submitStatus = ref('')

const submitForm = async () => {
  // In a real application, you would send this to a backend
  // For now, we'll just show a success message and save to localStorage
  try {
    const contactData = {
      ...formData.value,
      timestamp: new Date().toISOString()
    }

    // Save to localStorage as a simple solution
    const contacts = JSON.parse(localStorage.getItem('contact_submissions') || '[]')
    contacts.push(contactData)
    localStorage.setItem('contact_submissions', JSON.stringify(contacts))

    submitMessage.value = t('contact.successMessage')
    submitStatus.value = 'success'

    // Reset form
    formData.value = {
      name: '',
      email: '',
      subject: '',
      message: ''
    }

    // Clear message after 3 seconds
    setTimeout(() => {
      submitMessage.value = ''
    }, 3000)
  } catch (error) {
    submitMessage.value = t('contact.errorMessage')
    submitStatus.value = 'error'
  }
}

const goHome = () => {
  window.location.hash = '#/'
}
</script>

<style scoped>
.contact-page {
  min-height: 100vh;
  background: var(--color-bg-light);
}

/* Header */
.page-header {
  background: var(--gradient-primary);
  color: white;
  padding: var(--space-5xl) var(--space-base);
  text-align: center;
}

.page-header h1 {
  font-size: clamp(28px, 6vw, 48px);
  margin: 0 0 var(--space-base) 0;
  font-weight: var(--font-weight-bold);
}

.page-subtitle {
  font-size: var(--font-size-body);
  margin: 0;
  opacity: 0.95;
}

/* Content */
.container {
  max-width: 1000px;
  margin: 0 auto;
  padding: var(--space-4xl) var(--space-base);
}

.contact-content {
  background: var(--color-bg-light);
}

/* Contact Wrapper */
.contact-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3xl);
  margin-bottom: var(--space-3xl);
}

/* Form Section */
.contact-form-section h2,
.contact-info-section h2 {
  font-size: var(--font-size-h4);
  color: var(--color-text-primary);
  margin: 0 0 var(--space-2xl) 0;
  font-weight: var(--font-weight-bold);
}

.contact-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.form-group label {
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.form-group input,
.form-group textarea,
.form-group select {
  padding: var(--space-md);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: var(--font-size-body);
  font-family: var(--font-body);
  transition: all var(--transition-base) var(--ease-in-out));
  color: var(--color-text-primary);
  background: var(--color-bg-light);
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--color-primary-orange);
  box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.1);
}

.form-group textarea {
  resize: vertical;
  min-height: 120px;
}

.btn-submit {
  padding: var(--space-md) var(--space-2xl);
  background: var(--gradient-primary);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-body);
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out));
  font-family: var(--font-heading);
  margin-top: var(--space-md);
}

.btn-submit:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
}

.btn-submit:active {
  transform: translateY(-2px);
}

.submit-message {
  padding: var(--space-md);
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-semibold);
  text-align: center;
}

.submit-message.success {
  background: rgba(46, 204, 113, 0.1);
  color: var(--color-success);
  border: 1px solid var(--color-success);
}

.submit-message.error {
  background: rgba(231, 76, 60, 0.1);
  color: var(--color-danger);
  border: 1px solid var(--color-danger);
}

/* Info Section */
.contact-info-section {
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
}

.info-card {
  background: var(--color-bg-card);
  padding: var(--space-2xl);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border);
  text-align: center;
  transition: all var(--transition-base) var(--ease-in-out));
}

.info-card:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-4px);
}

.info-icon {
  font-size: 40px;
  margin-bottom: var(--space-md);
  display: block;
}

.info-card h3 {
  font-size: var(--font-size-h5);
  color: var(--color-text-primary);
  margin: 0 0 var(--space-sm) 0;
  font-weight: var(--font-weight-bold);
}

.info-card p {
  font-size: var(--font-size-body);
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.6;
}

.info-card a {
  color: var(--color-primary-orange);
  text-decoration: none;
  font-weight: var(--font-weight-semibold);
  transition: color var(--transition-base) var(--ease-in-out));
}

.info-card a:hover {
  color: var(--color-primary-gold);
}

/* Back Button */
.btn-back {
  display: block;
  margin: 0 auto;
  padding: var(--space-md) var(--space-2xl);
  background: var(--color-bg-light);
  color: var(--color-text-primary);
  border: 2px solid var(--color-border);
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out));
  font-family: var(--font-heading);
  font-size: var(--font-size-body);
}

.btn-back:hover {
  border-color: var(--color-primary-orange);
  color: var(--color-primary-orange);
  background: rgba(255, 107, 53, 0.05);
}

/* Responsive */
@media (max-width: 768px) {
  .contact-wrapper {
    grid-template-columns: 1fr;
    gap: var(--space-2xl);
  }
}

@media (max-width: 640px) {
  .page-header {
    padding: var(--space-3xl) var(--space-base);
  }

  .container {
    padding: var(--space-2xl) var(--space-base);
  }

  .contact-form-section h2,
  .contact-info-section h2 {
    font-size: var(--font-size-h5);
  }

  .btn-back {
    width: 100%;
  }
}
</style>
